import type { JSX, ReactNode } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import { signOutTaki } from '@taki/auth';
import type { TakiClaims, User } from '@taki/auth';
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatusBadge,
  repliDepuisConfig,
  useAccesPort,
  useBrandingPort,
} from '@taki/ui';
import { formatPlatformVersion, useBuildStamp } from '../buildStamp.js';
import type { BuildStampState } from '../buildStamp.js';

export interface AccountScreenProps {
  /** Config runtime du client (branding, orgId…). */
  config: TakiConfig;
  /** Utilisateur Firebase connecte. */
  user: User;
  /** Claims du token (orgId, orgRole). */
  claims: TakiClaims;
}

/**
 * Premiere lettre du nom affiche (a defaut, de l'email), en majuscule. Repli
 * "?". L'ORDRE SUIT CE QU'ON LIT A COTE : la pastille portait l'initiale de
 * l'EMAIL a cote d'un nom, donc un « T » a cote de « Celia ».
 */
function initialOf(user: User): string {
  const source = user.displayName ?? user.email ?? '';
  const first = source.trim().charAt(0);
  return first ? first.toUpperCase() : '?';
}

/** Ligne cle/valeur d'une carte : libelle discret a gauche, valeur a droite. */
function InfoRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-h-[1.75rem] items-center justify-between gap-4">
      <span className="text-sm text-ink-500">{label}</span>
      <div className="flex min-w-0 items-center gap-2 text-right">{children}</div>
    </div>
  );
}

/** Ce que la ligne "Version" affiche, deduit de l'etat de lecture du tampon. */
interface VersionDisplay {
  /** La valeur a l'ecran. Jamais un numero invente, jamais un `0.0.0` de repli. */
  text: string;
  /** L'infobulle : d'ou vient ce texte, ou pourquoi il n'y a pas de numero. */
  title: string;
  /** Vrai quand ce n'est pas un numero mais un constat : encre discrete. */
  muted: boolean;
}

/**
 * La ligne "Version" pour chacun des quatre etats du tampon de build.
 *
 * `null` = lecture en cours : la ligne garde sa hauteur et reste VIDE le temps
 * du chargement, plutot que d'afficher un texte qui serait remplace une frame
 * plus tard (meme choix que la bande de version du menu DEV).
 *
 * Un tampon ABSENT n'est pas une panne : un deploiement fabrique avant l'arrivee
 * du tampon n'en a pas, et le serveur de dev n'en sert pas toujours un. La ligne
 * le DIT alors en clair ; un vide se lirait comme un bug. La raison technique,
 * elle, reste dans l'infobulle.
 */
function versionDisplayOf(state: BuildStampState): VersionDisplay | null {
  switch (state.kind) {
    case 'loading':
      return null;
    case 'found':
      return {
        text: formatPlatformVersion(state.stamp.platformVersion),
        title: `Tampon ${
          state.stamp.mode === 'serve' ? 'servi par le serveur de dev' : 'du site fabrique'
        }, ${state.stamp.generatedAt}`,
        muted: state.stamp.platformVersion == null,
      };
    case 'missing':
      return {
        text: 'non disponible',
        title: `Aucun tampon de build : ${state.reason}`,
        muted: true,
      };
    case 'error':
      return { text: 'tampon illisible', title: state.message, muted: true };
  }
}

/**
 * Ecran "Mon compte". TROIS CHOSES, ET RIEN D'AUTRE : qui je suis (nom, email),
 * ou je suis (organisation, role reel, version qui tourne), et comment je sors
 * (deconnexion). Sobre et neutre : le lime n'apparait que sur le badge de role.
 *
 * CE QUI N'Y EST PLUS, ET POURQUOI. La gestion des utilisateurs etait une ligne
 * qui menait ailleurs : elle vit dans le hub Parametres, onglet Utilisateurs,
 * ou se fait le travail. Un raccourci vers un ecran ne fait pas une rubrique de
 * compte, et deux portes vers la meme page se contredisent tot ou tard. Les
 * "Preferences bientot" annonçaient un reglage qui n'existe pas : une promesse
 * a l'ecran est une dette, pas une information (le jour ou ces reglages
 * existent, ils naissent dans le hub Parametres, pas ici).
 *
 * LA VERSION NE S'ECRIT PAS ICI, ELLE SE LIT (`docs/VERSIONING.md`) : elle vient
 * du tampon de build `/taki-build.json`, par `useBuildStamp`. C'est le premier
 * endroit de l'interface, hors greffe DEV, qui dise ce qui tourne chez un
 * client : la question qu'on se pose en premier quand un client dit "ca ne
 * marche pas", et qui n'avait aucune reponse a l'ecran.
 */
export function AccountScreen({ config, user, claims }: AccountScreenProps): JSX.Element {
  const displayName = user.displayName ?? user.email ?? 'Utilisateur';
  /* L'EMAIL EST L'IDENTIFIANT REEL du compte (c'est la clef de la fiche membre,
     `docs/ACCES.md`) : il se lit sous le nom, sauf quand le nom EST l'email,
     auquel cas la repeter ne dirait rien de plus. */
  const identifiant = user.email != null && user.email !== displayName ? user.email : null;
  const buildStamp = useBuildStamp();
  /* Le nom de l'environnement vient du PORT (le choix du client), la config
     n'en est plus que le repli : la carte « Environnement » doit dire le meme
     nom que le login et que l'onglet du navigateur. */
  const courant = useBrandingPort()?.courant ?? repliDepuisConfig(config.branding);
  const version = versionDisplayOf(buildStamp);
  /*
   * LE ROLE VIENT DE LA FICHE D'ABORD, DES CLAIMS ENSUITE, et l'ordre compte :
   * une promotion ecrite dans la fiche membre s'applique tout de suite, alors
   * que les claims du jeton ne bougent qu'au rafraichissement suivant. Sans
   * port, ou avant la lecture, les claims restent la seule reponse honnete.
   */
  const acces = useAccesPort();
  const role = acces?.role ?? claims.orgRole ?? null;

  return (
    <div className="flex flex-col gap-6">
      {/* Profil : avatar + identite. */}
      <Card>
        <CardContent className="flex items-center gap-4 p-6">
          <Avatar size={46} src={user.photoURL} initials={initialOf(user)} />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-base font-semibold tracking-[-0.01em] text-ink-900">
              {displayName}
            </span>
            {identifiant != null && (
              <span className="truncate text-sm text-ink-500">{identifiant}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Environnement : instance, role, version qui tourne. */}
      <Card>
        <CardHeader>
          <CardTitle>Environnement</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3.5">
          {/* L'ORGANISATION SE LIT EN DEUX TEMPS : le nom du branding, qui dit a
              l'utilisateur ou il est, et l'identifiant technique en mono, qui
              dit au support QUEL environnement repond. L'un sans l'autre rate
              une des deux questions. */}
          <InfoRow label="Organisation">
            <span className="truncate text-sm text-ink-700">{courant.name}</span>
            <span className="shrink-0 font-mono text-xs text-ink-400" title="Identifiant de l'organisation">
              {config.orgId}
            </span>
          </InfoRow>
          <InfoRow label="Role">
            {role != null ? (
              <StatusBadge label={role} tone="accent" />
            ) : (
              <span className="text-sm text-ink-400">non defini</span>
            )}
          </InfoRow>
          <InfoRow label="Version">
            {version != null && (
              <span
                className={`truncate font-mono text-sm ${
                  version.muted ? 'text-ink-400' : 'text-ink-700'
                }`}
                title={version.title}
              >
                {version.text}
              </span>
            )}
          </InfoRow>
        </CardContent>
      </Card>

      {/* L'INFORMATION DES MEMBRES (RGPD, interet legitime) : ce que le journal
          d'audit garde d'eux, et combien de temps (`docs/JOURNAL.md`). */}
      <p className="px-1 text-xs text-ink-500">
        Vos connexions et vos gestes sur les réglages et les accès sont journalisés 12 mois pour la sécurité de l'environnement.
      </p>

      <div>
        <Button
          variant="danger"
          onClick={() => {
            void signOutTaki();
          }}
        >
          Se deconnecter
        </Button>
      </div>
    </div>
  );
}
